import {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import PageLoading from '../common/components/elements/PageLoading';
import axe from '../services/axios-client/axiosClient';
import { companyNamesById, productsList } from '../common/constants/index';

const productSlugById = {
  1: 'auto',
  2: 'moto',
  3: 'autocarro',
  4: 'profession',
  5: 'metlife',
  6: 'infodrive',
  8: 'travel',
  9: 'tutela-legale',
  10: 'commercial',
  11: 'casa',
  12: 'salute',
  13: 'ancillari-auto',
  14: 'sanitaria-stranieri',
  15: 'rc-professionale-sanita',
  1000: 'pubblico-impiego',
  1001: 'malattia',
  1002: 'infortunio',
  1003: 'condominio',
  1004: 'rc-famiglia',
};

const formatPrice = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
};

function QuotationCard({
  q,
  productName,
  companyName,
  clientName,
  vehiclePlate,
  onGo,
  onGoToRenewal = null,
  priceLabel = 'Prezzo:',
  expiryDate,
  effectiveDate,
  expiryMode,
  extraButton = null,
  showCompany = true,
  showQuoteTypeName = false,
}) {
  const formatedDate = expiryDate ? String(expiryDate).slice(0, 10) : '-';
  const formatedEffectiveDate = effectiveDate ? String(effectiveDate).slice(0, 10) : '-';
  const expiryLabel = expiryMode === 'semiannual' ? 'semestrale' : 'annuale';

  return (
    <div className="col-md-6">
      <div className="card border border-2 rounded-4 p-3 mb-2 h-100">
        <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
          <div>
            <div className="text-success fw-semibold mb-1">{priceLabel}</div>
            <div className="fs-4 fw-bold">
              €
              {formatPrice(q?.amount)}
            </div>
          </div>
          <div className="d-flex gap-2">
            {onGoToRenewal && (
              <button
                type="button"
                className="btn btn-primary btn-sm px-3"
                onClick={onGoToRenewal}
              >
                Vedi Rinnovo
              </button>
            )}
            <button
              type="button"
              className="btn btn-success btn-sm px-3"
              onClick={onGo}
            >
              Vedi Polizza
            </button>
          </div>
        </div>

        <div className="row g-2 mb-2">
          {clientName && (
            <div className="col-6">
              <div className="fw-semibold small">Cliente</div>
              <div className="text-muted">{clientName}</div>
            </div>
          )}

          {vehiclePlate && (
            <div className="col-6">
              <div className="fw-semibold small">Targa</div>
              <div className="text-muted">{vehiclePlate}</div>
            </div>
          )}

          {showCompany && (
            <div className="col-6">
              <div className="fw-semibold small">Compagnia</div>
              <div className="text-muted">{companyName}</div>
            </div>
          )}

          {showQuoteTypeName ? (
            <div className="col-6">
              <div className="fw-semibold small">Nome Prodotto</div>
              <div className="text-muted">
                {typeof q?.quote_type === 'string'
                  ? q?.quote_type
                  : (q?.quote_type?.name ?? '-')}
              </div>
            </div>
          ) : (
            <div className="col-6">
              <div className="fw-semibold small">Prodotto</div>
              <div className="text-muted">{productName}</div>
            </div>
          )}

          <div className="col-6">
            <div className="fw-semibold small">Data di decorrenza</div>
            <div className="text-muted">{formatedEffectiveDate}</div>
          </div>

          <div className="col-6">
            <div className="fw-semibold small">
              Data di scadenza
              {expiryLabel && ` ${expiryLabel}`}
            </div>
            <div className="text-muted">{formatedDate}</div>
          </div>
        </div>

        {extraButton && (
          <div className="mt-2 pt-2 border-top d-flex justify-content-center">
            {extraButton}
          </div>
        )}
      </div>
    </div>
  );
}

function RequestBlock({
  req,
  getProductNameById,
  getCompanyNameById,
  getProductSlugById,
  navigate,
  priceLabelDefault = 'Prezzo:',
  expiryMode,
}) {
  const [showOther, setShowOther] = useState(false);

  const quotations = req?.quotations || [];

  const getQuoteTypeSlug = useCallback((q) => {
    if (!q) return null;
    if (typeof q.quote_type === 'string') return q.quote_type;
    return q.quote_type?.slug;
  }, []);

  const resCivile = quotations.find((q) => getQuoteTypeSlug(q) === 'res_civile');

  const otherQuotations = quotations.filter((q) => {
    if (!q) return false;
    const slug = getQuoteTypeSlug(q);
    if (slug === 'res_civile') return false;
    if (resCivile && String(q.company_id) === String(resCivile.company_id)) return false;
    return true;
  });

  const processingQuotation = req?.processing_quotation;

  const expiryDate = expiryMode === 'semiannual'
    ? (req?.semiannual_expiry_date || req?.date)
    : (req?.annual_expiry_date || req?.date);

  const effectiveDate = req?.effective_date || req?.policy_effective_date;

  const goToRenewal = useCallback(() => {
    const token = req?.processing_quotation?.renewal_request_token;
    const slug = getProductSlugById(req?.product_id);

    if (!token || !slug) return;

    if (slug === 'auto' || slug === 'autocarro') {
      navigate(`/quotes/${token}`);
    } else {
      navigate(`/quotes/${slug}/${token}`);
    }
  }, [navigate, req, getProductSlugById]);

  const openPolizza = useCallback((q) => {
    const slug = getProductSlugById(q?.product_id);
    if (!slug || !q?.id) return;
    navigate(`/quotes/purchased/${q.id}/quotation/${slug}`);
  }, [navigate, getProductSlugById]);

  const mainQuote = resCivile || quotations[0];

  const previousCompanyLabel = req?.vehicle?.previousCompany?.label
    ?? req?.vehicle?.previous_company?.label
    ?? req?.vehicle?.previous_company_label
    ?? req?.moto?.previousCompany?.label
    ?? req?.moto?.previous_company?.label
    ?? req?.moto?.previous_company_label
    ?? req?.previous_company_label;

  return (
    <div className="mb-4 p-4 border border-2 rounded-4 bg-white">
      <span className="fw-semibold fs-5 mb-2">
        Polizza
        {' '}
        {req?.product_name || getProductNameById(req?.product_id) || '-'}
      </span>

      {previousCompanyLabel && (
        <div className="mt-2">
          <span className="fw-semibold small">Compagnia assicurativa attuale:</span>
          <span className="text-muted ms-2">{previousCompanyLabel}</span>
        </div>
      )}

      <div className="row gx-3 gy-3 mt-3 align-items-stretch">
        {mainQuote && (
          <QuotationCard
            q={mainQuote}
            priceLabel={priceLabelDefault}
            companyName={getCompanyNameById(mainQuote.company_id)}
            productName={req?.product_name || getProductNameById(mainQuote.product_id)}
            clientName={req?.company_name || req?.client_name}
            vehiclePlate={req?.moto_plate || req?.vehicle_plate}
            onGo={() => openPolizza(mainQuote)}
            expiryDate={expiryDate}
            effectiveDate={effectiveDate}
            expiryMode={expiryMode}
            onGoToRenewal={processingQuotation?.renewal_request_token ? goToRenewal : null}
            extraButton={
              otherQuotations.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center btn-sm"
                  onClick={() => setShowOther((v) => !v)}
                >
                  <span className="me-2">
                    <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M10 2a8 8 0 1 1 0 16A8 8 0 0 1 10 2Zm0 3.5a.75.75 0 0 0-.75.75v3.5a.75.75 0 0 0 1.5 0V6.25A.75.75 0.0 0 10 5.5Zm0 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                        fill="#6F42C1"
                      />
                    </svg>
                  </span>

                  <span>
                    {otherQuotations.length}
                    {' '}
                    garanz
                    {otherQuotations.length === 1 ? 'ia' : 'ie'}
                    {' '}
                    inserit
                    {otherQuotations.length === 1 ? 'a' : 'e'}
                  </span>
                  <span className="ms-2">{showOther ? '▲' : '▼'}</span>
                </button>
              )
            }
          />
        )}

        {otherQuotations.length > 0 && showOther && (
          <div className="row gx-3 gy-3 mt-2">
            {otherQuotations.map((q, idx) => (
              <QuotationCard
                key={q?.id ?? idx}
                q={q}
                priceLabel={priceLabelDefault}
                companyName={getCompanyNameById(q.company_id)}
                productName={req?.product_name || getProductNameById(q.product_id)}
                clientName={req?.company_name || req?.client_name}
                vehiclePlate={req?.moto_plate || req?.vehicle_plate}
                onGo={() => openPolizza(q)}
                expiryDate={expiryDate}
                effectiveDate={effectiveDate}
                expiryMode={expiryMode}
                onGoToRenewal={processingQuotation?.renewal_request_token ? goToRenewal : null}
                showCompany={false}
                showQuoteTypeName
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, items, children }) {
  if (!items?.length) return null;

  return (
    <>
      <div className="mb-3 mt-5">
        <span className="fw-semibold fs-5">
          {title}
          :
        </span>
        <span className="ms-2 text-danger">
          {items.length}
          {' '}
          in scadenza
        </span>
      </div>
      {children}
    </>
  );
}

export default function ExpirationDetails(props) {
  const { onClose } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const day = props.day ?? (query.get('day') ? Number(query.get('day')) : null);
  const month = props.month ?? (query.get('month') ? Number(query.get('month')) : null);
  const year = props.year ?? (query.get('year') ? Number(query.get('year')) : null);

  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const getCompanyNameById = useCallback((id) => {
    const c = companyNamesById.find((comp) => String(comp.id) === String(id));
    return c ? c.name : '-';
  }, []);

  const getProductNameById = useCallback((id) => {
    const p = productsList.find((prod) => String(prod.id) === String(id));
    return p ? p.name : '-';
  }, []);

  const getProductSlugById = useCallback((id) => productSlugById[id], []);

  useEffect(() => {
    let cancelled = false;

    async function loadDetails() {
      if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return;

      setLoading(true);
      try {
        setLoading(true);
        setError(null);
        const resp = await axe.get('/expirations/details', { params: { day, month, year } });
        const data = resp?.data?.data || resp?.data;
        if (!cancelled) setDetails(data);
      } catch (err) {
        const payload = err?.response?.data || null;
        if (!cancelled) setError(payload || { error: 'Internal server error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [day, month, year]);

  const handleClose = () => {
    if (typeof onClose === 'function') onClose();
    else navigate(-1);
  };

  if (loading) {
    return <PageLoading />;
  }

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-0">Dettagli scadenze</h3>
          <small className="text-dark">{details?.date ?? '-'}</small>
        </div>
        <button type="button" className="btn btn-danger" onClick={handleClose}>
          Indietro
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error?.error ?? 'Errore caricamento dettagli'}
          {error?.id && (
            <div className="small mt-1">
              ID:
              {' '}
              {error.id}
            </div>
          )}
        </div>
      )}

      {loading && <PageLoading />}

      {!loading && (
        <>
          <Section title="Polizze Annuali" items={details?.annuals_requests}>
            <div className="row">
              {details?.annuals_requests?.map((req) => (
                <RequestBlock
                  key={req.id}
                  req={req}
                  navigate={navigate}
                  getCompanyNameById={getCompanyNameById}
                  getProductNameById={getProductNameById}
                  getProductSlugById={getProductSlugById}
                  priceLabelDefault="Prezzo:"
                  expiryMode="annual"
                />
              ))}
            </div>
          </Section>

          <Section title="Polizze Semestrali" items={details?.semiannuals_requests}>
            <div className="row">
              {details?.semiannuals_requests?.map((req) => (
                <RequestBlock
                  key={req.id}
                  req={req}
                  navigate={navigate}
                  getCompanyNameById={getCompanyNameById}
                  getProductNameById={getProductNameById}
                  getProductSlugById={getProductSlugById}
                  priceLabelDefault="Prezzo:"
                  expiryMode="semiannual"
                />
              ))}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
